import React, { useEffect, useMemo, useState } from 'react';
import { Search, Wrench, CheckSquare, Clock, AlertTriangle, Layers, Zap, TrendingUp, TrendingDown, Activity, BarChart2 } from 'lucide-react';
import { capaInsightsApi, CapaInsights, CapaCount } from '../../services/capaService';
import Toast, { type ToastMessage } from '../common/Toast';
import { CapaInsightsSkeleton } from '../common/InsightsSkeleton';
import NoDataFound from '../common/NoDataFound';
import { usePermission } from '../../contexts/PermissionContext';

const ACT_INSIGHTS = 'CAP_INVESTIGATIONS';

const EMPTY: CapaInsights = {
    in_investigation: 0, in_remediation: 0, pending_feedback: 0,
    avg_closure_days: 0, overdue_open: 0,
    by_bucket: [], by_category: [], by_source: [], impact_severity: [], recurrent: [],
};

const BUCKET_COLOR: Record<string, string> = { People: '#f97316', Process: '#3b82f6', Technology: '#8b5cf6', Environment: '#10b981', Unspecified: '#94a3b8' };
const SOURCE_COLOR: Record<string, string> = { Audit: '#ef4444', 'Incident Reporting': '#3b82f6', 'Control Test': '#10b981', 'Risk Assessment': '#8b5cf6', Compliance: '#f59e0b', Manual: '#94a3b8' };
const SEVERITY_COLOR: Record<string, string> = { Critical: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#10b981' };

const maxOf = (rows: CapaCount[]) => rows.reduce((m, r) => Math.max(m, r.count), 0);

const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: any) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
        style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s ease, transform 0.2s ease' }}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>
        {iconColor && <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />}
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: any) => {
    const maxCount = Math.max(...data.map((d: any) => d.count), 1);
    return (
        <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
            style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums" style={{ background: `${accentColor}12`, color: accentColor }}>{total}</span>}
            </div>
            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6 italic">No data yet.</p>
            ) : (
                <ul className="divide-y divide-slate-50 flex-1">
                    {data.map((item: any, idx: number) => {
                        const color = item.color || '#6366f1';
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{ width: barPct > 0 ? `${barPct}%` : '0%', background: `linear-gradient(90deg, ${color}bb, ${color})`, boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none' }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums" style={{ color: item.count > 0 ? color : '#94a3b8' }}>{item.count}</span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const InsightsTab = () => {
    const [data, setData] = useState<CapaInsights>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_INSIGHTS, 'view');

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoading(true);
        capaInsightsApi.get()
            .then(d => { if (!cancelled && d) setData(d); })
            .catch(e => { if (!cancelled) setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load insights' }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canView]);

    const sourceTotal = useMemo(() => data.by_source.reduce((s, r) => s + r.count, 0), [data.by_source]);
    const severityTotal = useMemo(() => data.impact_severity.reduce((s, r) => s + r.count, 0), [data.impact_severity]);
    const bucketTotal = useMemo(() => data.by_bucket.reduce((s, r) => s + r.count, 0), [data.by_bucket]);
    const categoryTotal = useMemo(() => data.by_category.reduce((s, r) => s + r.count, 0), [data.by_category]);

    const bucketData = data.by_bucket.map(b => ({ label: b.label, count: b.count, color: BUCKET_COLOR[b.label] || '#94a3b8' }));
    const categoryData = data.by_category.map(c => ({ label: c.label, count: c.count, color: '#6366f1' }));
    const sourceData = data.by_source.map(s => ({ label: s.label, count: s.count, color: SOURCE_COLOR[s.label] || '#94a3b8' }));
    const severityData = data.impact_severity.map(s => ({ label: s.label, count: s.count, color: SEVERITY_COLOR[s.label] || '#94a3b8' }));

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to CAPA insights." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <CapaInsightsSkeleton />}
                    {!loading && (
                        <div className="py-4 space-y-4">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ROW_capa_insights_pipeline">
                                <KpiCard label="In Investigation" value={data.in_investigation} sub="Under active review" icon={Search} iconBg="#eff6ff" iconColor="#3b82f6" />
                                <KpiCard label="In Remediation" value={data.in_remediation} sub="Fix in progress" icon={Wrench} iconBg="#fff7ed" iconColor="#f97316" />
                                <KpiCard label="Pending Feedback" value={data.pending_feedback} sub={`Avg closure: ${data.avg_closure_days} days`} icon={CheckSquare} iconBg="#ecfdf5" iconColor="#10b981" />
                            </div>
                            {data.overdue_open > 0 && (
                                <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex gap-2 items-center" style={{ boxShadow: '0 1px 4px rgba(239,68,68,0.08)' }}>
                                    <AlertTriangle size={13} className="text-red-500 shrink-0" />
                                    <p className="text-xs text-red-700"><span className="font-semibold">{data.overdue_open}</span> open investigation{data.overdue_open === 1 ? '' : 's'} past due date.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <DistributionWidget title="By RCA Bucket" data={bucketData} icon={Layers} total={bucketTotal} accentColor="#f97316" data-testid="CARD_capa_insights_failures" />
                                <DistributionWidget title="By Cause Category" data={categoryData} icon={Activity} total={categoryTotal} accentColor="#6366f1" />
                                <DistributionWidget title="By Source" data={sourceData} icon={Clock} total={sourceTotal} accentColor="#3b82f6" data-testid="CARD_capa_insights_source" />
                                <DistributionWidget title="Impact Severity" data={severityData} icon={BarChart2} total={severityTotal} accentColor="#ef4444" />
                            </div>
                            {data.recurrent.length > 0 && (
                                <div className="rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
                                    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white">
                                        <div className="p-1.5 rounded-lg" style={{ background: '#f59e0b12' }}><Zap size={12} style={{ color: '#f59e0b' }} /></div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Top Recurrent Failure Reasons — Last 90 Days</span>
                                    </div>
                                    <ul className="divide-y divide-slate-50" data-testid="LST_capa_recurrent">
                                        {data.recurrent.map(row => (
                                            <li key={row.label} className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                                style={{ gridTemplateColumns: '1fr 6rem 5rem' }}
                                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{row.label}</span>
                                                <span className="text-[11px] text-slate-500 tabular-nums">{row.count} {row.count === 1 ? 'cause' : 'causes'}</span>
                                                <span className={`flex items-center gap-1 text-[11px] font-bold justify-end ${row.rising ? 'text-red-500' : 'text-emerald-600'}`}>
                                                    {row.rising ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                                    {row.trend_pct > 0 ? '+' : ''}{row.trend_pct}%
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default InsightsTab;
export { InsightsTab };
